import { Injectable, OnDestroy } from '@angular/core';
import {
    CurrentPersonDto,
    DateFormatService,
    LoggedInUser,
    PageTitle,
    PersonsOrganisationDto,
    UserService,
    UserStatus
} from '@flyfreely-portal-ui/flyfreely';
import { deepEqual } from 'fast-equals';
import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    Subject,
    combineLatest
} from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

export interface LoadingOrganisations {
    type: 'loading';
}

export interface CurrentOrganisation {
    type: 'organisation_loaded';
    organisation: PersonsOrganisationDto;
}

export interface NoAccessToOrganisation {
    type: 'no_access';
}

export interface NoLicenceToOrganisation {
    type: 'no_licence';
    organisation: PersonsOrganisationDto;
}

export interface NoOrganisation {
    type: 'no_organisation';
}

export type SelectedOrganisation =
    | LoadingOrganisations
    | CurrentOrganisation
    | NoAccessToOrganisation
    | NoLicenceToOrganisation
    | NoOrganisation;

@Injectable({
    providedIn: 'root'
})
export class WorkspaceStateService implements OnDestroy {
    private currentOrganisationSubject =
        new BehaviorSubject<SelectedOrganisation>({ type: 'loading' });

    public currentOrganisation$ =
        this.currentOrganisationSubject.asObservable();
    public currentLoadedOrganisation$ = this.currentOrganisationSubject.pipe(
        filter<CurrentOrganisation>(o => o.type === 'organisation_loaded')
    );

    private organisation: PersonsOrganisationDto;
    private organisationId$ = new ReplaySubject<number | undefined>(1);
    private organisationId?: number;

    private ngUnsubscribe$ = new Subject<void>();

    user$: Observable<CurrentPersonDto>;

    constructor(
        private userService: UserService,
        private pageTitleService: PageTitle,
        private dateFormatService: DateFormatService
    ) {
        this.organisationId$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                organisationId => (this.organisationId = organisationId)
            );

        this.user$ = userService.userChange$.pipe(
            filter(c => c.type === UserStatus.LOGGED_IN),
            // FIXME This casting isn't needed in VSCode, but the TS compiler is complaining
            map(c => (<LoggedInUser>c).currentUser),
            distinctUntilChanged(),
            takeUntil(this.ngUnsubscribe$)
        );

        this.currentOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(selectedOrganisation => {
                if (selectedOrganisation.type === 'organisation_loaded') {
                    this.pageTitleService.refreshOrganisation(
                        selectedOrganisation.organisation
                    );
                    // Update date/time formats when organisation loads
                    this.dateFormatService.setFormats(
                        selectedOrganisation.organisation.unitsPreferences
                    );
                } else {
                    this.pageTitleService.refreshOrganisation(null);
                    // Reset to default formats when no organisation
                    this.dateFormatService.resetToDefaults();
                }
            });

        combineLatest([
            this.organisationId$.pipe(distinctUntilChanged()),
            userService.userChange$.pipe(
                filter(c => c.type === UserStatus.LOGGED_IN),
                distinctUntilChanged(deepEqual)
            )
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([organisationId, user]) =>
                this.notifyOrganisationChanged(
                    organisationId,
                    (<LoggedInUser>user).currentUser,
                    (<LoggedInUser>user).currentUsersOrganisations
                )
            );
    }

    ngOnDestroy() {
        this.currentOrganisationSubject.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    public getCurrentOrganisation() {
        return this.currentOrganisationSubject.getValue();
    }

    public changeOrganisation(organisationId: number) {
        if (organisationId === this.organisationId) {
            return;
        }
        this.organisationId$.next(organisationId);

        this.userService.setAdditionalOrganisation(undefined);
    }

    private notifyOrganisationChanged(
        organisationId: number | undefined,
        user: CurrentPersonDto | undefined,
        organisations: PersonsOrganisationDto[]
    ) {
        if (organisationId == null) {
            this.currentOrganisationSubject.next({ type: 'no_organisation' });
            this.organisation = null;
            return;
        }

        const oldOrganisation = this.organisation;

        this.organisation = organisations?.find(o => o.id === organisationId);

        if (
            oldOrganisation === this.organisation &&
            this.organisation != null
        ) {
            return;
        }
        if (user == null) {
            return;
        }

        if (this.organisation == null) {
            if (user.type === 'SYSTEM_ADMIN') {
                this.userService
                    .fetchOrganisationForUser(organisationId)
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe({
                        next: organisation => {
                            this.organisation = organisation;
                            this.userService.setAdditionalOrganisation(
                                organisation
                            );
                            this.sendNotification(this.organisation, user.type);
                        },
                        error: () => {
                            this.currentOrganisationSubject.next({
                                type: 'no_access'
                            });
                        }
                    });
            } else {
                this.currentOrganisationSubject.next({ type: 'no_access' });
            }
        } else {
            this.sendNotification(this.organisation, user.type);
        }
    }

    private sendNotification(
        organisation: PersonsOrganisationDto,
        userType: CurrentPersonDto.Type
    ) {
        if (organisation == null) {
            return;
        }
        const updateType =
            organisation.hasLicence ||
            organisation.owner ||
            userType === 'SYSTEM_ADMIN'
                ? 'organisation_loaded'
                : 'no_licence';

        this.currentOrganisationSubject.next({
            type: updateType,
            organisation
        });

        // if (
        //     oldOrganisation == null ||
        //     oldOrganisation.id !== this.organisation.id
        // ) {
        //     this.changeOrganisation(this.organisation.id);
        // }
    }
}
